<template>
  <b-modal
    :id="modalId"
    title="Create Brochure"
    size="lg"
    role="dialog"
    modal-class="regular"
    centered
    no-close-on-backdrop
    :ok-title="okTitle"
    :ok-only="okOnly"
    :ok-disabled="okDisabled"
    @ok="onModalOk"
    @shown="onModalShown"
  >
    <template slot="default">
      <div class="custom-modal-content">
        <div v-if="isStateInitial">
          <b-row>
            <b-col>
              <b-container class="label">Title:<span class="important-label">*</span></b-container>
              <b-form-input
                ref="brochureTitle"
                v-model="$v.brochure.title.$model"
                :state="validationUtils.validateState($v.brochure, 'title')"
                placeholder="Enter a title"
                class="modal-input"
              ></b-form-input>
              <b-form-invalid-feedback>
                This is a required field.
              </b-form-invalid-feedback>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-container class="label">Description:</b-container>
              <b-form-textarea
                v-model="brochure.description"
                placeholder="Enter a description (optional)"
                class="modal-input"
              ></b-form-textarea>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-container class="label">Models:<span class="important-label">*</span></b-container>
              <div class="table-responsive modal-input">
                <table class="table table-striped dataTable">
                  <thead>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>Actions</th>
                  </thead>
                  <tbody>
                    <tr v-for="(model, index) in brochure.models" :key="model.modelId">
                      <td>
                        {{ model.modelName }}
                      </td>
                      <td>
                        {{ model.modelAlias }}
                      </td>
                      <td>
                        <a href="javascript:void(0)" @click="onRemoveModelButtonClicked(index)" class="d-block d-md-inline">
                          <span class="icon icon-trash" />
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="label container"></div>

              <div class="modal-input">
                <b-form inline>
                  <label class="sr-only" for="brochure-model-being-added-model">Model Id</label>
                  <v-select
                    id="brochure-model-being-added-model"
                    v-model="brochureModelBeingAdded.model"
                    class="mb-2 mr-sm-2 mb-sm-0"
                    placeholder="-- Select a model --"
                    :options="allCompanionModels"
                    :value="null"
                    label="name"
                  >
                  </v-select>

                  <label class="sr-only" for="brochure-model-being-added-alias">Model Alias</label>
                  <b-input
                    id="brochure-model-being-added-alias"
                    v-model="brochureModelBeingAdded.alias"
                    class="mb-2 mr-sm-2 mb-sm-0"
                    placeholder="Enter an alias (optional)"
                  ></b-input>

                  <b-button
                    variant="blue"
                    :disabled="this.brochureModelBeingAdded.model == null || this.selectedBrochureModelAlreadyAdded()"
                    @click="onAddNewModelButtonClicked"
                  >
                    Add
                  </b-button>
                </b-form>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <span class="form-control is-invalid" style="display: none;"></span>
              <div
                class="error invalid-feedback"
                v-if="$v.brochure.models.$error && (!$v.brochure.models.required || !$v.brochure.models.minLength)"
              >
                Please add at least {{ $v.brochure.models.$params.minLength.min }}
                model{{ $v.brochure.models.$params.minLength.min > 1 ? "s" : "" }}.
              </div>
              <div
                class="error invalid-feedback"
                v-if="$v.brochure.models.$error && !$v.brochure.models.maxLength"
              >
                Please select at most {{ $v.brochure.models.$params.maxLength.max }}
                model{{ $v.brochure.models.$params.maxLength.max > 1 ? "s" : "" }}.
              </div>
              <div
                class="error invalid-feedback"
                v-if="this.selectedBrochureModelAlreadyAdded()"
              >
                This model has already been added.
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <div class="label container"></div>
              <b-form-checkbox
                v-model="brochure.qrCodeLocked"
                class="modal-input"
              >
                Make the brochure locked to the QR code
              </b-form-checkbox>
            </b-col>
          </b-row>
        </div>
        <div v-if="isStatePosting">
          <b-container class="custom-modal-message">
            <b-alert show variant="info">
              Please wait while the brochure is being added...
            </b-alert>
          </b-container>
        </div>
        <div v-if="isStateSuccess">
          <b-container class="custom-modal-message">
            <b-alert show variant="success">
              The brochure has been added successfully!
            </b-alert>
          </b-container>
        </div>
        <div v-if="isStateFailed">
          <b-container class="custom-modal-message">
            <b-alert show variant="danger">
              The brochure couldn't be added at this time. Please try again later.
            </b-alert>
          </b-container>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import * as harpraApi from "@/services/api.service";
import * as stringUtils from "@/utils/string-utils.js";
import * as validationUtils from "@/utils/validation-utils.js"
import modals from '@/enums/modal-types'

import Vue from "vue"
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css";

import { required, minLength, maxLength } from 'vuelidate/lib/validators'

Vue.component('v-select', vSelect)

const STATE_INITIAL = 0, STATE_POSTING = 1, STATE_SUCCESS = 2, STATE_FAILED = 3;
const MAX_MODELS_COUNT = 4;

export default {
  name: "AddBrochureModal",
  props: {
    modalId: String,
  },
  data() {
    return {
      brochure: this.createEmptyBrochure(),
      brochureModelBeingAdded: this.createEmptyBrochureModelBeingAdded(),
      allCompanionModels: [],
      currentState: STATE_INITIAL,
      okTitle: "",
      okDisabled: false,
      okOnly: false,
    }
  },
  computed: {
    stringUtils() {
      return stringUtils;
    },
    validationUtils() {
      return validationUtils;
    },
    isStateInitial() {
      return this.currentState === STATE_INITIAL;
    },
    isStatePosting() {
      return this.currentState === STATE_POSTING;
    },
    isStateSuccess() {
      return this.currentState === STATE_SUCCESS;
    },
    isStateFailed() {
      return this.currentState === STATE_FAILED;
    },
  },
  validations: {
    brochure: {
      title: {
        required,
      },
      models: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(MAX_MODELS_COUNT),
      },
    },
  },
  methods: {
    onModalShown() {
      this.transitionToState(STATE_INITIAL);
    },
    transitionToState(state) {
      this.currentState = state;

      switch (state) {
        case STATE_INITIAL: {
          this.okTitle = "Create";
          this.okDisabled = false;
          this.okOnly = false;
          this.resetForm();
          break;
        }

        case STATE_POSTING: {
          this.okDisabled = true;
          this.okOnly = true;
          break;
        }

        case STATE_SUCCESS:
        case STATE_FAILED: {
          this.okTitle = "Close";
          this.okDisabled = false;
          this.okOnly = true;
          break;
        }
      }
    },
    resetForm() {
      this.brochure = this.createEmptyBrochure();
      this.brochureModelBeingAdded = this.createEmptyBrochureModelBeingAdded();
      this.populateAllCompanionModels();
      this.$v.$reset();

      this.$forceNextTick(() => {
        this.$refs.brochureTitle.focus();
      });
    },
    createEmptyBrochure() {
      return {
        title: "",
        description: "",
        qrCodeLocked: false,
        models: [],
      };
    },
    createEmptyBrochureModelBeingAdded() {
      return {
        model: null,
        alias: "",
      }
    },
    populateAllCompanionModels() {
      this.$AuthService.getAccessToken(
        async () => {
          this.allCompanionModels =
            await harpraApi.getModels(
              this.$AuthService.token.accessToken,
              harpraApi.StorageType.Companion);
        });
    },
    selectedBrochureModelAlreadyAdded() {
      if (!this.brochure ||
        !this.brochure.models ||
        !this.brochureModelBeingAdded ||
        this.brochureModelBeingAdded.model == null) {
        return false;
      }

      let alreadyAdded =
        this.brochure.models
          .filter(m => m.modelId === this.brochureModelBeingAdded.model.guid)
          .length > 0;

      return alreadyAdded;
    },
    onModalOk(modalEvent) {
      modalEvent.preventDefault();

      switch (this.currentState) {
        case STATE_INITIAL: {
          this.$v.$touch();

          if (this.$v.$anyError) {
            return;
          }

          if (this.brochure.models.length === 0) {
            return;
          }

          this.addBrochure();

          break;
        }
        case STATE_SUCCESS:
        case STATE_FAILED: {
          this.$nextTick(() => {
            this.$bvModal.hide(modals.addBrochureModal)
          });

          break;
        }
      }
    },
    addBrochure() {
      this.transitionToState(STATE_POSTING);

      this.$AuthService.getAccessToken(async () => {
        try {
          const formData = this.brochure;

          await harpraApi.postBrochure(this.$AuthService.token.accessToken, formData);

          this.transitionToState(STATE_SUCCESS);

          this.$emit("brochure-posted");
        } catch (error) {
          this.transitionToState(STATE_FAILED);

          this.$emit("brochure-post-failed");
        }
      }, () => {
        this.transitionToState(STATE_FAILED);

        this.$emit("brochure-post-failed");
      });
    },
    onAddNewModelButtonClicked() {
      if (!this.brochureModelBeingAdded.model) {
        return;
      }

      if (this.brochure.models.length >= MAX_MODELS_COUNT) {
        return;
      }

      if (this.selectedBrochureModelAlreadyAdded()) {
        return;
      }

      let newBrochureModel = {
        modelId: this.brochureModelBeingAdded.model.guid,
        modelName: this.brochureModelBeingAdded.model.name,
        modelAlias: this.brochureModelBeingAdded.alias,
      };

      this.brochure.models.push(newBrochureModel);

      this.brochureModelBeingAdded = this.createEmptyBrochureModelBeingAdded();
    },
    onRemoveModelButtonClicked(modelIndex) {
      this.brochure.models.splice(modelIndex, 1);
    },
  },
  created() {
  },
  mounted() {
  },
}
</script>
